import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { BADGE, TypeRankingsEntities } from 'client/data/models/type-rankings-entities';
import { getGenerationText } from 'site-modules/shared/utils/upper-funnel/type-rankings';
import { Link } from 'site-modules/shared/components/link/link';
import { DetailsField } from 'site-modules/upper-funnel/components/type/rankings-vehicle-card/card-details/details-field';
import { renderTopRatedBadge } from 'client/images/inline-svgs/ads/top-rated-2023';

const NOT_AVAILABLE_TEXT = 'Not available';

export function CardHeader({
  modelYear,
  rating = null,
  isLargeCard = false,
  badges = null,
  headingId = null,
  coreLink = '',
  modelDisplayOverride = null,
  uniqueId,
  isSubtypeExpansionPage = false,
}) {
  const { modelName, makeName, generations, year } = modelYear;
  const edmundsRatingContent = rating ? (
    <span>
      {rating.toFixed(1)}
      <sub className="size-14 fw-normal bottom-0">/10</sub>
    </span>
  ) : (
    NOT_AVAILABLE_TEXT
  );
  const generationText = getGenerationText(generations);
  const isTopRated = badges && badges.find(badge => Object.values(BADGE).includes(badge));
  const MmyTag = isSubtypeExpansionPage ? 'h2' : 'h3';

  return (
    <div
      className={classnames({
        'd-flex bg-blue-100 px-1_25 py-1 large-card-heading align-items-center mb-1_75 mb-md-1': isLargeCard,
        'text-cool-gray-30 mb-0_5': !isLargeCard,
      })}
      style={!isLargeCard ? { minHeight: '106px' } : undefined}
    >
      <div className={classnames({ 'mb-0_25': !isLargeCard })} style={isLargeCard ? { flex: '1' } : undefined}>
        {isTopRated && isLargeCard && renderTopRatedBadge()}
        {isTopRated && !isLargeCard && (
          <figure className="mb-0" style={{ marginTop: '-24px' }}>
            {renderTopRatedBadge()}
          </figure>
        )}

        {generationText && <div className="small">{generationText}</div>}
        <MmyTag
          id={headingId}
          className={classnames('mmy-header pe-1 mb-0', {
            'text-blue-30': isLargeCard,
            'mb-0 text-blue-50': !isLargeCard,
          })}
          data-test="core-page-link"
        >
          <Link
            to={coreLink}
            className="vehicle-card-link"
            style={
              isLargeCard
                ? undefined
                : {
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }
            }
            data-tracking-id="veh_card_core_page_nav"
            data-tracking-value="model_review"
          >
            <span className="visually-hidden">Read full review about</span>
            {`${year} ${makeName} ${modelDisplayOverride || modelName}`}
          </Link>
        </MmyTag>
      </div>
      <DetailsField
        name="Edmunds Rating"
        value={edmundsRatingContent}
        id={uniqueId}
        customClass={classnames('text-cool-gray-10', {
          'ps-1_5 pe-0_5d-flex flex-column justify-content-center': isLargeCard,
          'mt-auto': !isLargeCard,
        })}
        dtClass={classnames('text-cool-gray-30', {
          'size-12 fw-normal': isLargeCard,
          'd-none': !isLargeCard,
        })}
        ddClass={classnames('fw-bold', {
          'order-first size-24 w-100 text-center': isLargeCard,
          'size-20 pb-0_5': !isLargeCard,
        })}
      />
    </div>
  );
}

CardHeader.propTypes = {
  modelYear: TypeRankingsEntities.ModelYear.isRequired,
  uniqueId: PropTypes.string.isRequired,
  rating: PropTypes.number,
  isLargeCard: PropTypes.bool,
  badges: TypeRankingsEntities.Badges,
  coreLink: PropTypes.string,
  headingId: PropTypes.string,
  modelDisplayOverride: PropTypes.string,
  isSubtypeExpansionPage: PropTypes.bool,
};
